import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './PersonInfo.css';
import { useLanguage } from 'hooks/useLanguage';

/**
 * Докладна інформація про людину в керівництві — ADR-0008.
 *
 * Було: два повні дублі розмітки (десктоп і мобільний), вибір між ними за
 * window.screen.availWidth, порахованим під час рендеру; чотири значки,
 * намальовані вручну шляхами FontAwesome; розміри у vw; хрестик як div з
 * псевдоелементами і без доступної назви.
 *
 * Стало: одна розмітка, справжнє модальне вікно з Esc, блокуванням прокрутки
 * тіла та поверненням фокуса.
 */
export default function PersonInfo({ person, managementType, onClose }) {
  const language = useLanguage();
  const apiBase = process.env.REACT_APP_BASE_API_URL || '';
  const dialogRef = useRef(null);

  useEffect(() => {
    document.body.classList.add('is-locked');

    function handleKeyDown(event) {
      if (event.key === 'Escape') onClose();
    }

    document.addEventListener('keydown', handleKeyDown);
    dialogRef.current?.focus({ preventScroll: true });

    return () => {
      document.body.classList.remove('is-locked');
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const photo = person.photo?.url;
  const fullName = `${person.surname || ''} ${person.name || ''}`.trim();
  const role = managementType === 'council' ? person.council_role : person.region_role;
  const town = person.town;
  const description = person.description;

  // Портал у document.body — ADR-0008. Раніше вікно рендерилося прямо в дереві
  // сторінки (глибоко всередині <main>), і на прокрученій сторінці body.is-locked
  // (overflow: hidden, блокування скролу під час відкритого вікна) підрізало
  // fixed-нащадка по межі власного боксу body в поточній позиції скролу: вікно
  // малювалося під підвалом і взагалі не на весь екран. Портал прямо в body
  // прибирає цю залежність від глибини вкладеності в DOM.
  return createPortal(
    <div className='person-details-wrapper' onClick={onClose}>
      <div
        className='person-detail-card'
        role='dialog'
        aria-modal='true'
        aria-labelledby='person-detail-name'
        ref={dialogRef}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type='button'
          className='management-cross'
          aria-label={language === 'en' ? 'Close' : 'Закрити'}
          onClick={onClose}
        >
          <svg width='20' height='20' viewBox='0 0 24 24' aria-hidden='true'>
            <path d='M6 6l12 12M18 6L6 18' stroke='currentColor' strokeWidth='1.6' strokeLinecap='round' />
          </svg>
        </button>

        <div className='person-details-content-wrapper'>
          <div className='person-detail-media'>
            {photo ? (
              <img src={`${apiBase}${photo}`} alt='' className='person-detail-photo' />
            ) : (
              <span className='person-detail-photo person-detail-photo--empty' aria-hidden='true' />
            )}
          </div>

          <div className='person-detail-info'>
            <h2 className='person-detail-name' id='person-detail-name'>
              {fullName}
            </h2>

            <dl className='person-detail-facts'>
              {role && (
                <div>
                  <dt>{language === 'en' ? 'Role' : 'Посада'}</dt>
                  <dd>{role}</dd>
                </div>
              )}
              {town && (
                <div>
                  <dt>{language === 'en' ? 'Town' : 'Місто'}</dt>
                  <dd>{town}</dd>
                </div>
              )}
              {person.phone && (
                <div>
                  <dt>{language === 'en' ? 'Phone' : 'Телефон'}</dt>
                  <dd>
                    <a className='tnum' href={`tel:${person.phone}`}>
                      {person.phone}
                    </a>
                  </dd>
                </div>
              )}
              {person.email && (
                <div>
                  <dt>E-mail</dt>
                  <dd>
                    <a href={`mailto:${person.email}`}>{person.email}</a>
                  </dd>
                </div>
              )}
            </dl>

            {description && description !== 'null' && (
              <div className='person-detail-story'>
                <h3 className='person-detail-story-title'>{language === 'en' ? 'Biography' : 'Історія'}</h3>
                <p className='person-detail-description'>{description}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
